import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { useParams } from '@tanstack/react-router';
import { bbox as turfBBOX } from '@turf/turf';
import { useEffect } from 'react';
import { MapProvider } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import authorizations, { Action } from '~/authorizations';
import type { MapStyle } from '~/common/types';
import { MapComponent } from '~/components2/Map/';
import type { InitialViewState } from '~/components2/Map/common';
import MapContextMenu from '~/components2/Map/Widgets/MapContextMenu/MapContextMenu';
// watch utils
import type { ProjectDetailsParams } from '..';
import ProjectDataLayers from '../map/ProjectDataLayers';
import PrintViewMap from './PrintView/PrintViewMap';
import store from './store';
import EditorSurface from './surfaces/EditorSurface';
// additional map widgets and chilren
import Surface from './surfaces/Surface';
import Comments from './widgets/Comments';
import ObjectDetails from './widgets/ObjectDetails';
import ImageFullscreen from './widgets/ObjectDetails/ImagePreview/ImageFullscreen';
import ImageHoverPreview from './widgets/ObjectDetails/ImagePreview/ImageHoverPreview';
import StreetView from './widgets/StreetView';
import Toolbar from './widgets/Toolbar/Toolbar';
import TopLeftSection from './widgets/TopLeftSection';

export default function CivilDraw() {
  const { mapId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { project, projectId, currentMap, currentMapId, printView } = useSnapshot(store) as typeof store;

  // NOTE: Project maps are initially loaded in ./MapList.tsx
  // MapList handles the list of maps in the left navigation panel.
  useEffect(() => {
    store.undoStack = [];
    store.redoStack = [];
    store.allObjectIds = [];
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (currentMap.isLoading && projectId && mapId) {
      store.setCurrentMapId(mapId);
    }

    return () => {
      store.resetCurrentObjectId();
      store.resetCurrentMapState();
    };
  }, [mapId, projectId]);

  if (!project || !currentMap || project.isLoading || currentMap.isLoading) {
    return <SkeletonSpinnerCentered />;
  }

  const projectBounds = turfBBOX(project.geometry as unknown as GeoJSON.Feature);

  const initialViewState: InitialViewState = {
    bounds: [projectBounds[0], projectBounds[1], projectBounds[2], projectBounds[3]],
    fitBoundsOptions: {
      padding: 100,
    },
  };

  const onBasemapChange = (baseMap: MapStyle) => {
    // only send update if baseMaps differ.
    if (baseMap !== currentMap.baseMap) {
      store.updateMap({ baseMap });
    }
  };

  return (
    <div id="civildraw-map" className="flex h-full w-full">
      {printView ? (
        <PrintViewMap />
      ) : (
        <MapProvider>
          <MapComponent
            store={store}
            initialViewState={initialViewState}
            projectGeometry={project.geometry}
            mapStyle={currentMap.baseMap}
            onBasemapChange={onBasemapChange}
            showLayers={true}
            showLayerPicker={true}
            showScale={true}
            units={currentMap.unitSystem}
            showZoomButtons={true}
            showBasemapPicker={true}
            showLegend={true}
            includeDefaultContextMenu={false}
            projectId={projectId}
            currentMapId={currentMapId}
            id="map"
            controls={{ topLeft: [<TopLeftSection key={'key'} />] }}
            allowRotation={true}
          >
            <MapContextMenu
              store={store}
              allowComments={true}
              allowObjectOrdering={true}
              streetViewMode="internal"
              projectId={projectId}
              currentMapId={currentMapId}
            >
              <Surface interactive={authorizations.can(Action.EditCivilDraw)} />
              {/* NOTE: place components below Surface component if they are to be inaccessible when a modal appears */}
              <EditorSurface />
            </MapContextMenu>

            <ProjectDataLayers projectId={projectId} />

            <StreetView store={store} />

            <ImageHoverPreview />
            <ImageFullscreen />
            <ObjectDetails />
            <Toolbar />
            <Comments />
          </MapComponent>
        </MapProvider>
      )}
    </div>
  );
}
